import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useParams } from "react-router-dom";
import {
  AuraLayout,
  CashSpecialIcon,
  Close,
  GradientText,
  Layout,
  Navbar,
  SupportV2Icon,
} from "lola-framework-ui-test";
import { Trans, useTranslation } from "react-i18next";
import type { IOperationResponse } from "../services/api";
import { cancelOperation, getOperation } from "../services/api";
import { useOperationSSE } from "../hooks/useOperationSSE";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { APP_ROUTES, flavorClassName } from "../constants";
import { addBusinessDays, amountFormatter } from "../utils";
import { Popup } from "../components/Popup";
import { NavbarBackChevron } from "../components/NavbarBackChevron";
import type { TFunction } from "i18next";
import {
  CheckCircleIcon,
  ErrorCircleIcon,
  MoreCircleIcon,
  WarningCircleIcon,
} from "../assets/icons";
import { useClaimLink } from "../hooks/useClaimLink";
import type { ISessionState } from "../shared/session/interfaces/session";
import { LegalModal } from "../components/legal/LegalModal";
import "../styles/legal.style.css";
import { safeCapture } from "../utils/posthog";
import { PayoutInfo } from "../components/PayoutInfo";

interface SummaryItem {
  status: string;
  value: string | React.ReactNode;
  isBold?: boolean;
  isComplete?: boolean;
}

const buildStatusField = (status: string) => {
  switch (status) {
    case "completed":
      return {
        icon: <CheckCircleIcon />,
        value: status,
        color: "#31C859",
      };
    case "failed":
    case "error":
      return {
        icon: <ErrorCircleIcon />,
        value: status,
        color: "#FF3B30",
      };
    case "processing":
      return {
        icon: <MoreCircleIcon />,
        value: status,
        color: "#A3A3A3",
      };
    case "cancelled":
      return {
        icon: <ErrorCircleIcon bgColor="#A3A3A3" />,
        value: status,
        color: "#A3A3A3",
      };
    default:
      return {
        icon: <WarningCircleIcon />,
        value: status,
        color: "#F28D19",
      };
  }
};

const buildSummaryItems = (
  operation: IOperationResponse,
  t: TFunction<"translation", undefined>,
  session: ISessionState
): SummaryItem[] => [
  {
    status: t("receiptSummary.fields.total.payment"),
    value: `${amountFormatter(
      operation.totalCost +
        (operation.fundingFee ?? 0) +
        (operation.taxFee ?? 0) +
        (operation.transactionFee ?? 0)
    )} ${operation.sourceCurrency}`,
  },
  {
    status: t("receiptSummary.fields.receiver"),
    value: `${session.data?.userData?.name?.firstName} ${session.data?.userData?.name?.lastName}`,
  },
  {
    status: t("receiptSummary.fields.receiving"),
    value: `${amountFormatter(operation.receiveAmount)} ${
      operation.destinationCurrency
    }`,
  },
  {
    status: t("receiptSummary.fields.transfer.amount"),
    value: `${amountFormatter(operation.sendAmount)} ${
      operation.sourceCurrency
    }`,
  },
  {
    status: t("receiptSummary.fields.funding.fee"),
    value: `${amountFormatter(operation.fundingFee ?? 0)} ${
      operation.sourceCurrency
    }`,
  },
  {
    status: t("receiptSummary.fields.transaction.fee"),
    value: `${amountFormatter(operation.transactionFee ?? 0)} ${
      operation.sourceCurrency
    }`,
  },
  {
    status: t("receiptSummary.fields.tax.fee"),
    value: `${amountFormatter(operation.taxFee ?? 0)} ${
      operation.sourceCurrency
    }`,
  },
  {
    status: t("receiptSummary.fields.exchange.rate"),
    value: `${operation.exchangeRate.toFixed(4)} ${
      operation.destinationCurrency
    }/${operation.sourceCurrency}`,
  },
  {
    status: t("receiptSummary.fields.trasnaction.date"),
    value: `${new Date(operation.createdAt).toLocaleDateString()} \n ${new Date(
      operation.createdAt
    ).toLocaleTimeString()}`,
  },
  {
    status: t("receiptSummary.fields.date.available"),
    value: `${addBusinessDays(new Date(operation.createdAt))}`,
  },
  {
    status: t("receiptSummary.fields.status"),
    value: (
      <span
        className="span-status"
        style={{
          color: buildStatusField(operation.status).color,
        }}
      >
        {buildStatusField(operation.status).icon}{" "}
        {t(
          `receiptSummary.fields.status.items.${
            buildStatusField(operation.status).value
          }`
        )}
      </span>
    ),
    isBold: true,
    isComplete: operation.status === "completed",
  },
];

const CANCELLABLE_STATUSES = ["pending", "processing", "created", "completed"];
const TERMINAL_STATUSES = ["failed", "cancelled"];

/**
 * Validates if the operation was created within the last 30 minutes
 * @param createdAt - ISO string date when the operation was created
 * @returns true if 30 minutes or less have passed since creation
 */
const isWithin30Minutes = (createdAt: string): boolean => {
  const createdDate = new Date(createdAt);
  const now = new Date();
  const diffInMs = now.getTime() - createdDate.getTime();
  const diffInMinutes = diffInMs / (1000 * 60);
  return diffInMinutes <= 30;
};

export const ReceiptSummary = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const {
    theme,
    operations,
    setOperations,
    updateOperationFields,
    setAmountState,
  } = useStore();
  const { session } = useSessionStore();
  const { operationId } = useParams<{ operationId: string }>();
  const { redirectTo } = useRouteWithToken();

  const [showConfirm, setShowConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelResult, setCancelResult] = useState<"success" | "error" | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [showTransactionPolicyModal, setShowTransactionPolicyModal] =
    useState(false);
  const detailViewedRef = useRef(false);

  useOperationSSE(
    session.data?.linkId,
    operationId ? { watchOperationId: operationId } : undefined
  );

  const operation = useMemo(
    () => operations.find((op) => op.operationId === operationId) ?? null,
    [operations, operationId]
  );

  useEffect(() => {
    if (operation || !operationId) return;

    const linkId = session.data?.linkId;
    if (!linkId) return;

    setLoading(true);
    getOperation(linkId, operationId)
      .then((fetched) => {
        // Read the latest list from the store so we don't overwrite operations
        // added (e.g. via SSE) while the request was in flight.
        setOperations([...useStore.getState().operations, fetched]);
      })
      .catch((err) => {
        console.error("Failed to fetch operation:", err);
      })
      .finally(() => setLoading(false));
  }, [operation, operationId, session.data?.linkId, setOperations]);

  useEffect(() => {
    if (operation && !detailViewedRef.current) {
      detailViewedRef.current = true;
      safeCapture("ui.send.receipt.screen.view", {
        operation_id: operation.operationId,
        status: operation.status,
        send_amount: operation.sendAmount,
        receive_amount: operation.receiveAmount,
        source_currency: operation.sourceCurrency,
        destination_currency: operation.destinationCurrency,
      });
    }
  }, [operation]);

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, dropzoneColors } = colorConfig;
  // Button/CTA colors come from theme.lightness via the kit — not OS foreground.
  const ctaColor = textColorBtn;

  const summaryItems = operation
    ? buildSummaryItems(operation, t, session)
    : [];
  const canCancel =
    operation &&
    CANCELLABLE_STATUSES.includes(operation.status) &&
    !operation.isCancellationRequested &&
    isWithin30Minutes(operation.createdAt);

  const isCancelling =
    operation &&
    operation.isCancellationRequested &&
    !TERMINAL_STATUSES.includes(operation.status);

  const handleTransferAgain = () => {
    safeCapture("ui.send.receipt.transfer_again_button.click", {
      operation_id: operation?.operationId,
      status: operation?.status,
    });
    setAmountState({ amount: operation?.sendAmount.toString() ?? "0" });
    redirectTo(APP_ROUTES.TRANSFER_PAGE);
  };

  const handleCancelConfirm = useCallback(async () => {
    const linkId = session.data?.linkId;
    if (!linkId || !operationId) return;

    safeCapture("ui.send.receipt.cancel_button.click", {
      operation_id: operationId,
    });

    setCancelling(true);
    setCancelResult(null);
    try {
      await cancelOperation(linkId, operationId);
      updateOperationFields(operationId, { isCancellationRequested: true });
      setCancelResult("success");
      safeCapture("ux.send.receipt.operation.cancelled", {
        operation_id: operationId,
        success: true,
      });
    } catch {
      setCancelResult("error");
      safeCapture("ux.send.receipt.operation.cancelled", {
        operation_id: operationId,
        success: false,
        error_code: "CANCEL_FAILED",
      });
    } finally {
      setCancelling(false);
      setShowConfirm(false);
    }
  }, [session.data?.linkId, operationId, updateOperationFields]);

  if (!operation) {
    return (
      <AuraLayout
        colorConfig={colorConfig}
        className={`receipt-summary-section ${flavorClassName}`}
      >
        <Layout.Header>
          <NavbarBackChevron
            onBackClick={() => redirectTo(APP_ROUTES.TRANSACTIONS_PAGE)}
          >
            <Navbar
              title=""
              align="center"
              onBackClick={() => redirectTo(APP_ROUTES.TRANSACTIONS_PAGE)}
              icon={<SupportV2Icon colors={dropzoneColors} />}
              onIconClick={() => {
                safeCapture("ui.send.receipt.support_button.click");
                redirectTo(APP_ROUTES.SUPPORT_PAGE);
              }}
            />
          </NavbarBackChevron>
        </Layout.Header>
        <Layout.Content>
          <section className="receipt-summary-container">
            {loading ? (
              <p className="foreground">
                {t("receiptSummary.loading", "Loading...")}
              </p>
            ) : (
              <p className="foreground">
                {t("receiptSummary.notFound", "Operation not found")}
              </p>
            )}
          </section>
        </Layout.Content>
      </AuraLayout>
    );
  }

  const [intPart, decPart] = operation.sendAmount.toFixed(2).split(".");

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className={`receipt-summary-section ${flavorClassName}`}
      >
        <Layout.Header>
          <section className="receipt-header-wrapper">
            {/* Same .lola-navbar svg chip styles as the support icon */}
            <div className="lola-navbar receipt-header-close-slot">
              <Close
                size={26}
                className="lola-navbar--back-icon"
                aria-label="Close"
                onClick={() => redirectTo(APP_ROUTES.TRANSACTIONS_PAGE)}
              />
            </div>
            <Navbar
              title={t("transfer.summary.title")}
              align="center"
              noBackButton
              onBackClick={() => redirectTo(APP_ROUTES.TRANSACTIONS_PAGE)}
              icon={<SupportV2Icon colors={dropzoneColors} />}
              onIconClick={() => {
                safeCapture("ui.send.receipt.support_button.click");
                redirectTo(APP_ROUTES.SUPPORT_PAGE);
              }}
            />
          </section>
        </Layout.Header>
        <Layout.Content isOverflowauto>
          <section className="receipt-summary-container">
            <section className="receipt-summary-wrapper">
              <section className="summary-section gradient-card">
                <section className="summary-title">
                  <p className="foreground">
                    {t("receiptSummary.youveTransferred", "You sent")}
                  </p>
                  <h1 className="foreground">
                    ${intPart}.<label>{decPart}</label>
                  </h1>
                  <p className="foreground transaction-id">
                    <b>{t("receiptSummary.transactionId", "Transaction ID")}</b>
                    <span>
                      {operation.operationId.slice(0, 8).toUpperCase()}
                    </span>
                  </p>
                </section>
                <section className="summary-info">
                  {summaryItems.map(
                    ({ status, value, isBold, isComplete }, index) => (
                      <SummaryInfoItem
                        key={index}
                        status={status}
                        value={value}
                        isBold={isBold}
                        isComplete={isComplete}
                      />
                    )
                  )}
                </section>
              </section>

              {cancelResult === "success" && (
                <GradientText textColor="#F28D19" className="cancel-feedback">
                  {t("receiptSummary.cancelPending")}
                </GradientText>
              )}
              {cancelResult === "error" && (
                <GradientText textColor="#ef4444" className="cancel-feedback">
                  {t("receiptSummary.cancelError")}
                </GradientText>
              )}
              {!cancelResult && isCancelling && (
                <GradientText textColor="#F28D19" className="cancel-feedback">
                  {t("receiptSummary.cancelPending")}
                </GradientText>
              )}

              <PayoutInfo
                title={t("receiptSummary.transferAgain", "Transfer again")}
                subtitle=""
                icon={
                  <span
                    className="icon-wrapper"
                    style={{ "--bgr": backgroundBtn } as CSSProperties}
                  >
                    <CashSpecialIcon
                      size={30}
                      colors={[ctaColor, ctaColor]}
                      className="icon-bg-br exchange-quote-info bg-transparent"
                    />
                  </span>
                }
                className="no-icons"
                background={backgroundBtn}
                color={ctaColor}
                onChange={handleTransferAgain}
              />

              {canCancel && !cancelResult && (
                <button
                  type="button"
                  className="cancel-operation-btn"
                  onClick={() => setShowConfirm(true)}
                >
                  {t("receiptSummary.cancelTransfer")}
                </button>
              )}
            </section>
            <section className="footer-section transactions-footer">
              <p className="footer-text muted-foreground text-light">
                <Trans i18nKey="receiptSummary.transferNote" />
              </p>
              <p
                className="footer-text foreground link"
                style={{ cursor: "pointer" }}
                onClick={() => redirectTo(APP_ROUTES.CANCELLATION_PAGE)}
              >
                {t("receiptSummary.cancelationPolicy", "Cancelation Policy")}
              </p>
            </section>
          </section>
        </Layout.Content>
      </AuraLayout>
      <LegalModal
        doc="transactionPolicy"
        visible={showTransactionPolicyModal}
        onClose={() => setShowTransactionPolicyModal(false)}
      />
      <Popup
        visible={showConfirm}
        onClose={() => setShowConfirm(false)}
        className="pupup-cancel"
        showCloseBtn={false}
      >
        <section className="cancel-confirm-slot">
          <h2 className="foreground text-center h2">
            {t("receiptSummary.cancelConfirmTitle")}
          </h2>
          <p className="cancel-confirm-message foreground">
            {t("receiptSummary.cancelConfirmMessage")}
          </p>
          <div className="cancel-confirm-actions">
            <button
              style={{ background: backgroundBtn }}
              color={textColorBtn}
              onClick={() => setShowConfirm(false)}
              disabled={cancelling}
              className="cancel-confirm-btn cancel-confirm-btn--cancel"
            >
              {t("receiptSummary.cancelConfirmNo")}
            </button>
            <button
              type="button"
              className="cancel-confirm-btn cancel-confirm-btn--danger"
              onClick={handleCancelConfirm}
              disabled={cancelling}
            >
              {cancelling ? "..." : t("receiptSummary.cancelConfirmYes")}
            </button>
          </div>
        </section>
      </Popup>
    </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  isComplete?: boolean;
  status: string;
  value: string | React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  isComplete,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p className="foreground status">{status}</p>
    <p
      className={`foreground ${isComplete ? "completed" : "muted-foreground"} ${
        value === "Failed" ? "text-error" : ""
      }`}
      style={{ whiteSpace: "pre-line", textAlign: "right" }}
    >
      {value}
    </p>
  </section>
);
